import { useCallback, useEffect, useState } from "react";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { CreditCard, ExternalLink, RefreshCw } from "lucide-react";

function explainResult(data, fallback) {
  const message = data?.message || data?.bse2?.message || fallback;
  if (["FAILED", "NOT_CONFIGURED", "NO_BSE_ORDERS"].includes(data?.status)) {
    toast.error(message);
  } else if (["UNKNOWN", "SUBMITTING", "WAITING_2FA"].includes(data?.status) || String(data?.live_status || "").toUpperCase() === "UNKNOWN") {
    toast.warning(message);
  } else {
    toast.success(message);
  }
}

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await clientApi.get("/payments");
      setPayments(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, after) => {
    setBusy(key);
    try {
      const { data } = await fn();
      after(data);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setBusy("");
    }
  };

  const openPg = (payment) => act(`pg-${payment.payment_ref}`,
    () => clientApi.post(`/payments/${payment.payment_ref}/bse-pg-url`),
    (data) => {
      if (data.pg_url) {
        window.open(data.pg_url, "_blank", "noopener,noreferrer");
        toast.success("BSE payment page opened in a new tab");
      } else explainResult(data, "BSE did not return a payment-page URL. Complete order authentication and check again.");
    });

  const checkStatus = (payment) => act(`status-${payment.payment_ref}`,
    () => clientApi.get(`/payments/${payment.payment_ref}/bse-status`),
    (data) => explainResult(data, `BSE payment status: ${data.live_status || data.status || "unknown"}`));

  return (
    <ClientLayout>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Payments</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Payment links and statuses are fetched directly from BSE StAR MF.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading real payment records…</div> :
            payments.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]">No payments have been initiated.</div> :
            <div className="divide-y divide-[var(--glass-border)]">{payments.map((payment) => {
              const bse = payment.bse2 || {};
              return <div key={payment.payment_ref} className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-3">
                  <CreditCard className="mt-0.5 h-5 w-5 text-[var(--cta)]" />
                  <div>
                    <div className="font-medium">{payment.payment_ref} · {formatCurrency(payment.total_amount)}</div>
                    <div className="text-xs text-[var(--ink-muted)]">Orders {payment.order_numbers || "—"} · {payment.mode_of_payment} · {formatDateTime(payment.created_at)}</div>
                    <div className="mt-1 text-xs text-[var(--ink-muted)]">{bse.message || "Awaiting BSE status"}</div>
                    {bse.live_status && <div className="mt-1 text-xs font-medium">BSE live status: {bse.live_status}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{payment.status}</Badge>
                  <Badge className={bse.status === "SUBMITTED" ? "bg-emerald-600" : bse.status === "FAILED" ? "bg-red-600" : "bg-amber-600"}>{bse.status || "NOT SENT"}</Badge>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => checkStatus(payment)}>
                    <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy === `status-${payment.payment_ref}` ? "animate-spin" : ""}`} /> Check BSE
                  </Button>
                  <Button size="sm" className="rounded-full bg-[var(--cta)]" disabled={!!busy} onClick={() => openPg(payment)}>
                    <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open BSE payment
                  </Button>
                </div>
              </div>;
            })}</div>}
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
